import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FinancasOffice,
  createFinancasOffice,
  listFinancasOffices,
} from '../../api/financasOffices';
import './FinancasOfficePicker.css';

const ADD_OFFICE = '__add_office__';

export const officeLabel = (office: Pick<FinancasOffice, 'name' | 'code'>): string =>
  office.code ? `${office.name} (${office.code})` : office.name;

interface FinancasOfficePickerProps {
  // The selected office id, or null when nothing is selected yet.
  value: string | null;
  // Name to show for `value` when it is not in the active list (for example
  // an office that has since been deactivated).
  valueName?: string | null;
  onChange: (office: FinancasOffice) => void;
  disabled?: boolean;
  id?: string;
}

// Select of the active Finanças offices plus an inline "Add office" form. The
// lawyer builds the office list as she files, so adding one must not leave the
// page: a new office is created and selected in one step.
const FinancasOfficePicker: React.FC<FinancasOfficePickerProps> = ({
  value,
  valueName,
  onChange,
  disabled = false,
  id = 'financas-office-select',
}) => {
  const { currentUser } = useAuth();
  const [offices, setOffices] = useState<FinancasOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      setLoading(true);
      setLoadError('');
      try {
        const token = await currentUser.getIdToken();
        const result = await listFinancasOffices(token);
        if (!cancelled) setOffices(result);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Failed to load Finanças offices.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    if (selected === ADD_OFFICE) {
      setAdding(true);
      setAddError('');
      return;
    }
    const office = offices.find(o => o.id === selected);
    if (office) onChange(office);
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewName('');
    setNewCode('');
    setAddError('');
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) {
      setAddError('Enter the office name.');
      return;
    }
    if (!currentUser) return;
    setSaving(true);
    setAddError('');
    try {
      const token = await currentUser.getIdToken();
      const office = await createFinancasOffice(token, name, newCode.trim() || null);
      setOffices(prev => {
        const others = prev.filter(o => o.id !== office.id);
        return [...others, office].sort((a, b) => a.name.localeCompare(b.name));
      });
      cancelAdd();
      onChange(office);
    } catch (error) {
      setAddError(error instanceof Error ? error.message : 'Failed to add the office.');
    } finally {
      setSaving(false);
    }
  };

  const valueMissingFromList = Boolean(value) && !offices.some(o => o.id === value);

  return (
    <div className="financas-office-picker">
      <select
        id={id}
        className="status-select financas-office-select"
        value={adding ? ADD_OFFICE : value || ''}
        onChange={handleSelect}
        disabled={disabled || loading || saving}
        aria-label="Finanças office"
      >
        <option value="" disabled>
          {loading ? 'Loading offices...' : 'Select office...'}
        </option>
        {valueMissingFromList && value && (
          <option value={value}>{valueName || value}</option>
        )}
        {offices.map(office => (
          <option key={office.id} value={office.id}>
            {officeLabel(office)}
          </option>
        ))}
        <option value={ADD_OFFICE}>+ Add office...</option>
      </select>
      {loadError && (
        <span className="financas-office-error" role="alert">{loadError}</span>
      )}
      {adding && (
        <div className="financas-office-add">
          <input
            type="text"
            className="financas-office-input"
            placeholder="Office name, e.g. Lisboa 3"
            aria-label="New office name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            disabled={saving}
            autoFocus
          />
          <input
            type="text"
            className="financas-office-input financas-office-input--code"
            placeholder="Code (optional)"
            aria-label="New office code"
            value={newCode}
            onChange={e => setNewCode(e.target.value)}
            disabled={saving}
          />
          <div className="financas-office-add-actions">
            <button type="button" className="financas-btn financas-btn--primary" onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add office'}
            </button>
            <button type="button" className="financas-btn" onClick={cancelAdd} disabled={saving}>
              Cancel
            </button>
          </div>
          {addError && (
            <span className="financas-office-error" role="alert">{addError}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default FinancasOfficePicker;
